/**
 * ブランドのサイトから提案を受ける (brand_site_proposal・docs/features/brand-product-restructure-plan.md §2.3.1)。
 *
 * - `POST /jobs/brand-site-proposal {url, quoted_credits, brand_id?}` → 202 {job_id}。有料 (credit_rate_config の
 *   brand_site_proposal / brand-site-proposal)・利用者 1 時間 10 回・組織 1 日 100 回
 * - 進捗・結果は GET /jobs/<id> (backend status.py が本機能専用に整形する)。進捗には段階 (stages: 完了 / 実行中 / 未実行 /
 *   スキップ) が載り、縦ステッパー (JobStepper) で見せる
 * - 結果は保存されない (7 日で消えるジョブ成果物)。名前 / タグライン / 説明 / 公式サイト判定 / トーン＆マナー / ロゴ候補を
 *   利用者が確認して適用する
 */
import { apiClient } from '../../services/apiClient';
import { waitForJobCompletion, type AsyncJob } from '../../services/asyncJobApi';
import { normalizeDesignLanguage, type BrandDesignLanguage } from './designLanguage';

export const SITE_PROPOSAL_CREDIT_RATE = { service_type: 'brand_site_proposal', resource_type: 'brand-site-proposal' } as const;

/** 料金一覧から 1 回のポイント数を取る (未設定・0 以下は null = 受け付けられない)。 */
export function siteProposalCreditsPerRun(
  rates: ReadonlyArray<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined,
): number | null {
  const rate = rates?.find((r) => r.service_type === SITE_PROPOSAL_CREDIT_RATE.service_type && r.resource_type === SITE_PROPOSAL_CREDIT_RATE.resource_type);
  const credits = rate ? Number(rate.credits_per_unit) : NaN;
  return Number.isFinite(credits) && credits > 0 ? credits : null;
}

export interface SiteProposalJobBody {
  url: string;
  quoted_credits: number;
  brand_id?: string | null;
}

export async function createBrandSiteProposalJob(body: SiteProposalJobBody): Promise<{ job_id: string; status: string }> {
  const response = await apiClient.post<{ job_id: string; status: string }>('/jobs/brand-site-proposal', body);
  return response.data;
}

export type JobStageStatus = 'done' | 'running' | 'pending' | 'skipped' | 'failed' | 'cancelled';
export interface JobStageState {
  key: string;
  status: JobStageStatus;
  label?: string;
}

export interface SiteProposalProgress {
  stage_key?: string | null;
  stages?: JobStageState[];
  step?: { key?: string; text?: string; at?: string } | null;
  notes?: string[];
  deadline_at?: string | null;
}

export interface SiteProposalIdentity {
  brand_name: string | null;
  tagline: string | null;
  description: string | null;
  company_name: string | null;
  /** ブランドコードの提案。AI の案から、他のブランドで使われていないことを確かめた 1 件 (無ければ null・古いジョブは undefined)。
   *  登録までに他で使われることはあり得るので、最終の判定は作成時の一意チェック (409) */
  brand_code?: string | null;
  official: {
    is_official: boolean | null;
    confidence: 'high' | 'medium' | 'low';
    official_url: string | null;
    reason: string | null;
  };
}

export interface SiteProposalLogo {
  index: number;
  source: string;
  url: string;
  content_type: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  /** ロゴとして登録できるか (SVG か、長辺 512px 以上のラスタ画像)。低解像度の候補は参考として見せるだけ */
  registrable?: boolean;
  signed_url: string | null;
}

export interface SiteProposalResult {
  identity: SiteProposalIdentity | null;
  design_language: unknown;
  screenshot_url: string | null;
  page_title: string | null;
  logos: SiteProposalLogo[];
  notes: string[];
  source_url: string | null;
}

export const SITE_PROPOSAL_STAGE_ORDER = ['capture', 'extract', 'logos', 'done'] as const;
export const SITE_PROPOSAL_STAGE_LABELS: Record<string, string> = {
  capture: 'サイトを撮影して読む',
  extract: 'AI が名前・配色・書体をまとめる',
  logos: 'ロゴの候補を集める',
  done: '完了',
};

export const SITE_PROPOSAL_REQUEST_ERROR_MESSAGES: Record<string, string> = {
  INVALID_URL: 'このURLは取得できません。公開されているWebページのURLを指定してください',
  UNSUPPORTED_SITE: 'このサイトは読み取りに対応していません。ブランドの公式サイトのURLを指定してください',
  HOURLY_LIMIT: 'サイトからの提案は1時間に10回までです。しばらく待ってからお試しください',
  DAILY_LIMIT: '本日の上限に達しました',
  BRAND_NOT_FOUND: 'ブランドが見つかりません',
  PRICE_CHANGED: '料金が更新されました。ブラウザーでページ全体を再読み込みして確認してください',
  RATE_NOT_CONFIGURED: 'サイトからの提案の料金が未設定のため、現在ご利用いただけません',
};

export const SITE_PROPOSAL_JOB_ERROR_MESSAGES: Record<string, string> = {
  LOGIN_WALL: 'このページはログインが必要か、アクセスが制限されているため読み取れません',
  ACCESS_RESTRICTED: 'このページはログインが必要か、アクセスが制限されているため読み取れません',
  NAVIGATION_BLOCKED: 'このページは別のサイトへ転送されるため読み取れません。公開されているページのURLを指定してください',
  NO_CONTENT: 'サイトからブランドの情報を読み取れませんでした',
  SUMMARY_FAILED: '提案をまとめられませんでした。時間をおいてお試しください',
  TIMEOUT: '読み取りに時間がかかりすぎたため中断しました。時間をおいてお試しください',
  CAPTURE_FAILED: 'ページを取得できませんでした。時間をおいてもう一度お試しください',
};

export function siteProposalRequestErrorMessage(err: unknown): string {
  const resp = (err as { response?: { status?: number; data?: { error?: string; error_code?: string } } })?.response;
  const code = resp?.data?.error_code;
  if (code && SITE_PROPOSAL_REQUEST_ERROR_MESSAGES[code]) return SITE_PROPOSAL_REQUEST_ERROR_MESSAGES[code];
  if (resp?.data?.error) return resp.data.error;
  if (resp?.status === 402) return 'ポイントが不足しています';
  return err instanceof Error && err.message ? err.message : '提案の開始に失敗しました';
}

export function siteProposalJobErrorMessage(err: unknown): string {
  const code = (err as { error_code?: string })?.error_code;
  if (code && SITE_PROPOSAL_JOB_ERROR_MESSAGES[code]) return SITE_PROPOSAL_JOB_ERROR_MESSAGES[code];
  const message = (err as { message?: string })?.message;
  return message && message !== code ? message : SITE_PROPOSAL_JOB_ERROR_MESSAGES.SUMMARY_FAILED;
}

export const SITE_PROPOSAL_GIVE_UP_AFTER_MS = 15 * 60 * 1000;

export async function waitForBrandSiteProposal(
  jobId: string,
  options?: { onProgress?: (job: AsyncJob) => void; signal?: AbortSignal; pollInterval?: number },
): Promise<SiteProposalResult> {
  const job = await waitForJobCompletion<SiteProposalResult>(jobId, {
    pollInterval: options?.pollInterval ?? 2000,
    maxDuration: SITE_PROPOSAL_GIVE_UP_AFTER_MS,
    skipVisibilityCheck: true,
    onProgress: options?.onProgress,
    signal: options?.signal,
  });
  return job.result_data;
}

/**
 * 進捗応答の stages が無い (古い backend) ときの既定: 現在の段階だけ running にする。
 * 受付直後で進捗が無いときは先頭の段階を running にする (止まって見えないように)
 */
export function siteProposalStages(progress: SiteProposalProgress | null | undefined): JobStageState[] {
  if (progress?.stages && progress.stages.length > 0) return progress.stages;
  const current = progress?.stage_key ?? SITE_PROPOSAL_STAGE_ORDER[0];
  let seen = false;
  return SITE_PROPOSAL_STAGE_ORDER.map((key) => {
    if (key === current) { seen = true; return { key, status: 'running' as const }; }
    return { key, status: seen ? ('pending' as const) : ('done' as const) };
  });
}

// ============================================================
// 提案の差分 (名前 / タグライン / 説明)。現在値が空なら既定で採用・入力済みは据え置き (案件 URL 読み取りと同じ規則)
// ============================================================

export type IdentityField = 'brand_name' | 'tagline' | 'description';
export const IDENTITY_FIELDS: readonly IdentityField[] = ['brand_name', 'tagline', 'description'];
export const IDENTITY_FIELD_LABELS: Record<IdentityField, string> = { brand_name: 'ブランド名', tagline: 'タグライン', description: '説明' };

export interface IdentityItem {
  key: IdentityField;
  label: string;
  current: string;
  proposed: string;
  /** 既定で採用するか (現在値が空で提案があるとき) */
  adopt: boolean;
}

export function buildIdentityItems(identity: SiteProposalIdentity | null, current: Partial<Record<IdentityField, string | null | undefined>>): IdentityItem[] {
  if (!identity) return [];
  const items: IdentityItem[] = [];
  for (const key of IDENTITY_FIELDS) {
    const proposed = (identity[key] ?? '').trim();
    if (!proposed) continue;
    const cur = (current[key] ?? '').trim();
    if (cur === proposed) continue;
    items.push({ key, label: IDENTITY_FIELD_LABELS[key], current: cur, proposed, adopt: !cur });
  }
  return items;
}

/** 提案のトーン＆マナーを、保存済み (またはフォーム) の値と合わせる: 利用できる書体 (brandFonts) は現在の値を引き継ぐ。 */
export function proposedDesignLanguage(result: SiteProposalResult, current: BrandDesignLanguage | null | undefined): BrandDesignLanguage | null {
  const normalized = normalizeDesignLanguage(result.design_language);
  if (!normalized) return null;
  // 成果物のスクリーンショットのパス (7 日で消える) はブランドに保存しない (backend の検証も brands/design_language/ 配下だけを通す)
  const { screenshotPath: _dropped, ...dl } = normalized;
  void _dropped;
  const kept = current?.typography.brandFonts;
  return kept ? { ...dl, typography: { ...dl.typography, brandFonts: [...kept] } } : dl;
}

export function officialNotice(identity: SiteProposalIdentity | null, sourceUrl: string | null): string | null {
  if (!identity) return null;
  const o = identity.official;
  if (o.is_official === false) {
    return o.official_url
      ? `読み取ったページは販売店や代理店のサイトの可能性があります (${o.reason ?? '根拠なし'})。公式サイトの候補: ${o.official_url}`
      : `読み取ったページは販売店や代理店のサイトの可能性があります (${o.reason ?? '根拠なし'})。公式サイトの URL は確認できませんでした`;
  }
  if (o.is_official === true && o.confidence !== 'high') {
    return `公式サイトと判定しました (確信度: ${o.confidence === 'medium' ? '中' : '低'}・${o.reason ?? '根拠なし'})。${sourceUrl ?? ''}`.trim();
  }
  return null;
}

// ============================================================
// ロゴ候補の登録 (B 群と CreateBrandModal 改で共用)
// ============================================================

/**
 * ロゴ候補を登録する: 署名 URL から画像を取り、/logos/upload → /brands/<id>/logos の順で登録する (既存のロゴ追加と同じ経路)。
 * 失敗は reject (呼び出し側が通知・再試行する)。
 */
export async function registerProposedLogo(brandId: string, logo: SiteProposalLogo): Promise<void> {
  if (!logo.signed_url) throw new Error('ロゴ候補の画像を取得できません');
  if (logo.registrable === false) throw new Error('低解像度のため登録できません');
  const blob = await (await fetch(logo.signed_url)).blob();
  const ext = (logo.content_type ?? 'image/png').split('/')[1]?.replace('svg+xml', 'svg').replace('x-icon', 'ico').replace('jpeg', 'jpg') ?? 'png';
  const file = new File([blob], `site-logo-${logo.index + 1}.${ext}`, { type: logo.content_type ?? 'image/png' });
  const formData = new FormData();
  formData.append('file', file);
  const uploaded = await apiClient.post<{ logo?: { id: string } }>('/logos/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  const logoId = uploaded.data.logo?.id;
  if (!logoId) throw new Error('ロゴのアップロードに失敗しました');
  await apiClient.post(`/brands/${brandId}/logos`, { logo_id: logoId, description: `サイトから取得 (${logo.source})` });
}

export function formatSiteProposalElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}分${s}秒` : `${s}秒`;
}
